import { formatValidQueryParamUrlCommaSeparated } from "utils/common";
import { sunoCommsApi } from "../api-base";
import type {
  ContactFile,
  ContactFull,
  ContactsFilter,
  LeadAction,
  LeadAddress,
  LeadContact,
  LeadEmail,
  LeadUser,
  PaginatedContactsResponse,
  UsersFilter
} from "../types/lead-contact.type";
import type { LeadPhoneNumber } from "../types/lead-number.type";

export const contactsEndpoints = sunoCommsApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getUsers: builder.query<LeadUser[], UsersFilter>({
      query: (filters) =>
        `/users/${formatValidQueryParamUrlCommaSeparated(filters, true)}`,
      providesTags: ["LeadUsers"]
    }),
    getContacts: builder.query<LeadContact[], ContactsFilter>({
      query: (filters) =>
        `/contacts/${formatValidQueryParamUrlCommaSeparated(filters, true)}`,
      providesTags: ["LeadContacts"]
    }),
    getContactsPaginated: builder.query<PaginatedContactsResponse, ContactsFilter>({
      query: (filters) =>
        `/contacts/${formatValidQueryParamUrlCommaSeparated({ ...filters, paginated: true }, true)}`,
      providesTags: ["LeadContacts"]
    }),
    getContactsCount: builder.query<number, ContactsFilter>({
      query: (filters) =>
        `/contacts/count${formatValidQueryParamUrlCommaSeparated(filters, true)}`,
      transformResponse: (response: { count: number }) =>
        Number(response?.count || 0),
      providesTags: ["LeadContactsCount"]
    }),
    getContactById: builder.query<ContactFull, string>({
      query: (id: string) => `/contacts/${id}`,
      providesTags: ["LeadContacts"]
    }),
    createContact: builder.mutation<ContactFull, Partial<LeadContact>>({
      query: (data) => ({
        url: `/contacts/`,
        method: "POST",
        body: data
      }),
      invalidatesTags: ["LeadContacts", "LeadContactsCount"]
    }),
    updateContact: builder.mutation<
      ContactFull,
      Partial<LeadContact> & { id: string }
    >({
      query: ({ id, ...data }) => ({
        url: `/contacts/${id}`,
        method: "PATCH",
        body: data
      }),
      invalidatesTags: ["LeadContacts", "LeadContactsCount"]
    }),
    updateContactStatus: builder.mutation<
      LeadContact,
      { contact_id: string; type: string }
    >({
      query: ({ contact_id, type }) => ({
        url: `/contacts/${contact_id}/statuses`,
        method: "POST",
        body: {
          type
        }
      }),
      invalidatesTags: ["LeadContacts", "LeadContactsCount"]
    }),
    updateContactLeadOwner: builder.mutation<
      LeadContact,
      { contact_id: string; lead_owner_id: string | null }
    >({
      query: ({ contact_id, lead_owner_id }) => ({
        url: `/contacts/${contact_id}`,
        method: "PATCH",
        body: {
          lead_owner_id
        }
      }),
      invalidatesTags: ["LeadContacts"]
    }),
    addContactPhone: builder.mutation<
      LeadPhoneNumber,
      Partial<LeadPhoneNumber> & { contact_id: string }
    >({
      query: (data) => ({
        url: `/contacts/${data.contact_id}/numbers`,
        method: "POST",
        body: data
      }),
      invalidatesTags: ["LeadContacts", "LeadContactsCount"]
    }),
    addContactEmail: builder.mutation<
      LeadEmail,
      Partial<LeadEmail> & { contact_id: string }
    >({
      query: (data) => ({
        url: `/contacts/${data.contact_id}/emails`,
        method: "POST",
        body: data
      }),
      invalidatesTags: ["LeadContacts", "LeadContactsCount"]
    }),
    addContactAddress: builder.mutation<
      LeadAddress,
      Partial<LeadAddress> & { contact_id: string }
    >({
      query: (data) => ({
        url: `/contacts/${data.contact_id}/addresses`,
        method: "POST",
        body: data
      }),
      invalidatesTags: ["LeadContacts", "LeadContactsCount"]
    }),
    addContactAction: builder.mutation<
      LeadAction,
      Partial<LeadAction> & { contact_id: string }
    >({
      query: (data) => ({
        url: `/contacts/${data.contact_id}/actions`,
        method: "POST",
        body: data
      }),
      invalidatesTags: ["LeadContacts", "LeadContactsCount"]
    }),
    getContactAsPatient: builder.query<any, string>({
      query: (id) => `/contacts/convert-to-patient/${id}`,
      providesTags: ["LeadPatients"]
    }),
    convertContactToPatient: builder.mutation<
      any,
      { id: string; location_id?: string | null }
    >({
      query: ({ id, location_id }) => ({
        url: `/contacts/convert-to-patient/${id}`,
        method: "POST",
        body: { location_id: location_id || undefined }
      }),
      invalidatesTags: ["LeadPatients", "LeadContacts", "LeadContactsCount"]
    }),
    uploadContactFiles: builder.mutation<
      ContactFile[],
      { contactId: string; files: File[] }
    >({
      query: ({ contactId, files }) => {
        const formData = new FormData();
        files.forEach((f) => formData.append("files", f));
        return {
          url: `/contacts/${contactId}/files`,
          method: "POST",
          body: formData
        };
      },
      invalidatesTags: ["LeadContacts"]
    }),
    deleteContactFile: builder.mutation<
      void,
      { contactId: string; fileId: string }
    >({
      query: ({ contactId, fileId }) => ({
        url: `/contacts/${contactId}/files/${fileId}`,
        method: "DELETE"
      }),
      invalidatesTags: ["LeadContacts"]
    })
  })
});

export const {
  useGetUsersQuery,
  useGetContactsQuery,
  useGetContactsPaginatedQuery,
  useGetContactsCountQuery,
  useGetContactByIdQuery,
  useCreateContactMutation,
  useUpdateContactMutation,
  useUpdateContactStatusMutation,
  useUpdateContactLeadOwnerMutation,
  useAddContactPhoneMutation,
  useAddContactEmailMutation,
  useAddContactAddressMutation,
  useAddContactActionMutation,
  useGetContactAsPatientQuery,
  useConvertContactToPatientMutation,
  useUploadContactFilesMutation,
  useDeleteContactFileMutation
} = contactsEndpoints;
